// Live job updates over server-sent events: status and progress events update the cached job, done refreshes job and graph data and ends the stream, and a dropped stream reconnects with Last-Event-ID and exponential backoff.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { JobEvent, OperationJob } from "@/api/__generated__";
import { jobsGetJobQueryKey } from "@/api/__generated__/@tanstack/react-query.gen";
import { zJobEvent } from "@/api/__generated__/zod.gen";
import { invalidateGraphData } from "@/api/keys";
import { isAbortError } from "@/api/problem";
import { streamEvents } from "@/api/sse";
import { invalidateJobQueries } from "@/features/jobs/queries";

export type JobStreamState = "connecting" | "live" | "reconnecting" | "closed";

const MAX_BACKOFF_MS = 30_000;
const FIRST_BACKOFF_MS = 1_000;

export function applyJobEvent(previous: OperationJob | undefined, event: JobEvent): OperationJob | undefined {
  if (event.event === "progress") {
    return previous === undefined ? previous : { ...previous, progress: event.progress };
  }
  return event.job;
}

export function backoffMs(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, FIRST_BACKOFF_MS * 2 ** Math.max(0, attempt - 1));
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

async function follow(
  jobId: string,
  queryClient: QueryClient,
  signal: AbortSignal,
  setState: (state: JobStreamState) => void,
): Promise<void> {
  const key = jobsGetJobQueryKey({ path: { job_id: jobId } });
  let lastEventId: string | undefined;
  let attempt = 0;
  while (!signal.aborted) {
    try {
      const stream = streamEvents(
        {
          path: `/api/v1/jobs/${encodeURIComponent(jobId)}/events`,
          signal,
          ...(lastEventId === undefined ? {} : { lastEventId }),
        },
        zJobEvent,
      );
      for await (const message of stream) {
        attempt = 0;
        setState("live");
        lastEventId = message.id ?? lastEventId;
        queryClient.setQueryData<OperationJob>(key, (previous) => applyJobEvent(previous, message.data));
        if (message.data.event === "done") {
          setState("closed");
          await Promise.all([invalidateJobQueries(queryClient), invalidateGraphData(queryClient)]);
          return;
        }
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
    }
    attempt += 1;
    setState("reconnecting");
    await wait(backoffMs(attempt), signal);
  }
}

export function useJobEvents(jobId: string, enabled: boolean): JobStreamState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<JobStreamState>(enabled ? "connecting" : "closed");
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const controller = new AbortController();
    void follow(jobId, queryClient, controller.signal, setState);
    return () => {
      controller.abort();
    };
  }, [jobId, enabled, queryClient]);
  return enabled ? state : "closed";
}
